import { CSSProperties } from "react";
import { ShowComedianRow } from "@punchup/data/legacy/shows";
import { TixologiResumedEvent } from "@punchup/data/legacy/tixologi";
import dayjs from "@punchup/utils/dayjs";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { withOpacity } from "@punchup/utils/colors";

/**
 * Theme a single CSS property to the venue accent color, or `undefined` when
 * there's no accent. Callers pair this with a `primary`-token fallback class
 * (e.g. `cn(!accentColor && "text-primary")`) so the unthemed default still
 * renders. Pass `opacity` to tint via `withOpacity`; `property` accepts custom
 * properties like `--tw-ring-color` for ring theming.
 */
export function accentStyle(
  accentColor: string | undefined,
  property: "color" | "backgroundColor" | "--tw-ring-color" = "color",
  opacity?: number
): CSSProperties | undefined {
  if (!accentColor) return undefined;
  const value =
    opacity != null ? withOpacity(accentColor, opacity) : accentColor;
  return { [property]: value } as CSSProperties;
}

export function getLowestPrice(event: TixologiResumedEvent): number | null {
  const visible = event.ticket_types?.filter((t) => t.ticket_visibility);
  if (!visible?.length) return null;
  const prices = visible
    .map((t) => t.initial_price)
    .filter((p): p is number => typeof p === "number");
  if (!prices.length) return null;
  return Math.min(...prices);
}

export function isAllSoldOut(event: TixologiResumedEvent): boolean {
  const visible = event.ticket_types?.filter((t) => t.ticket_visibility);
  if (!visible?.length) return false;
  return visible.every((t) => t.sold_out);
}

export function formatPrice(price: number): string {
  if (price === 0) return "Free";
  return `$${price.toFixed(2).replace(/\.00$/, "")}`;
}

export function formatEventTime(iso: string, tz?: string): string {
  // Computed in the venue/event timezone, but rendered without the zone
  // abbreviation — the date header above each group already establishes the
  // timezone context and the repeated "EDT" / "HST" was noisy.
  const d = dayjs(iso);
  if (tz) return d.tz(tz).format("h:mm A");
  return d.format("h:mm A");
}

export function formatEventDateHeader(iso: string, tz?: string): string {
  const d = tz ? dayjs(iso).tz(tz) : dayjs(iso);
  const includeYear = d.year() > dayjs().year();
  return d.format(includeYear ? "dddd, MMMM D, YYYY" : "dddd, MMMM D");
}

export { stripHtml } from "@punchup/utils/stripHtml";

export function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Minimal comedian shape needed to render a lineup avatar. Derived from a
 * show's `show_comedian_pairings` and passed from the server fetcher to the
 * client lineup components.
 */
export interface ShowGroupLineupMember {
  id: string;
  name: string;
  /** Resolved CDN URL for the comedian's avatar, or null when none is set. */
  imageUrl: string | null;
  isVerified: boolean;
  /** Comedian bio (`about`), shown in the tap-for-bio drawer / inline panel. */
  bio: string | null;
  /** Comedian slug, used to build the "View profile" link, or null when none. */
  slug: string | null;
}

/**
 * Convert a show's `show_comedians` rows into ordered lineup members. Sorted by
 * the pairing's `ordering` so the headliner (lowest ordering) comes first.
 */
export function toLineupMembers(
  showComedians: ShowComedianRow[]
): ShowGroupLineupMember[] {
  return [...showComedians]
    .sort((a, b) => a.ordering - b.ordering)
    .map((c) => ({
      id: c.id,
      name: c.display_name ?? "",
      imageUrl: c.about_img ? getCDNImageURL(c.about_img) : null,
      isVerified: !!c.is_punchup_verified,
      bio: c.about ?? null,
      slug: c.slug ?? null,
    }));
}

/**
 * True when every lineup in the list is non-empty and they all contain the
 * exact same set of comedians. Used to decide whether the group page can show a
 * single shared "Lineup" container instead of per-show lineups. An empty list,
 * or any lineup with no comedians, returns false.
 */
export function lineupsAreIdentical(
  lineups: ShowGroupLineupMember[][]
): boolean {
  if (lineups.length === 0) return false;
  if (lineups.some((lineup) => lineup.length === 0)) return false;

  const signature = (lineup: ShowGroupLineupMember[]): string =>
    lineup
      .map((m) => m.id)
      .sort()
      .join(",");

  const first = signature(lineups[0]);
  return lineups.every((lineup) => signature(lineup) === first);
}

export function groupEventsByDate(
  events: TixologiResumedEvent[],
  fallbackTz?: string
): { label: string; events: TixologiResumedEvent[] }[] {
  const buckets = new Map<string, TixologiResumedEvent[]>();
  for (const ev of events) {
    const tz = ev.time_zone || fallbackTz;
    const key = formatEventDateHeader(ev.start_date, tz);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(ev);
  }
  return Array.from(buckets.entries()).map(([label, events]) => ({
    label,
    events,
  }));
}
